import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { getCampaignMetrics } from '../../services/metaAdsService';
import { useMetaRead } from '../hooks/useMetaRead';
import { readMetaInteractions } from '../metaAnalysis';
import { formatMetaCount, formatMetaPct, formatMetaSeconds } from '../metaFormat';
import {
  MetaBarList, MetaBlockState, MetaCard, MetaEmptyLine, MetaGroupLabel, MetaStatRows, MetaStatTile,
} from './MetaDataParts';
import { MetaInteractionsSkeleton } from './MetaSkeletons';
import { metaCostText, metaFractions, validMetaScope } from './metaAnalysisUi';

const stat = (key, label, value, extra = {}) => ({ key, label, value, ...extra });
const count = (v) => formatMetaCount(v);

/** Pregunta con ayuda: el texto de la tasa y un ⓘ con su definición. */
function HelpLabel({ text, help }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {text}
      <Tooltip title={help} placement="top" arrow describeChild>
        <Box component="span" tabIndex={0} role="img" aria-label={help} sx={{ display: 'inline-flex', color: '#ABABAB', cursor: 'help' }}>
          <InfoOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
        </Box>
      </Tooltip>
    </Box>
  );
}

function VideoCard({ read }) {
  const { video, currency } = read;
  const retention = video.retention;
  const fractions = retention ? metaFractions(retention.map((r) => r.count)) : [];
  return (
    <MetaCard title="Video" testId="meta-interactions-video">
      {!read.hasVideo ? (
        <MetaEmptyLine testId="meta-interactions-video-empty">Tus anuncios de este período no tuvieron reproducciones de video.</MetaEmptyLine>
      ) : (
        <>
          <MetaStatRows
            testId="meta-interactions-video-rows"
            rows={[
              stat('plays', 'Reproducciones del video', count(video.plays)),
              stat('views3s', 'Reproducciones de 3 segundos', count(video.views3s)),
              stat('thruplays', 'Reproducciones ThruPlay (video completo o 15 s)', count(video.thruplays)),
              stat('avg', 'Tiempo promedio de reproducción', formatMetaSeconds(video.avgWatchTimeS)),
              ...(video.costPerThruplay != null && currency ? [stat('cost', 'Costo por ThruPlay', metaCostText(video.costPerThruplay, currency))] : []),
            ]}
          />
          {retention ? (
            <>
              <MetaGroupLabel>Cuánto del video se reprodujo</MetaGroupLabel>
              <MetaBarList
                testId="meta-interactions-retention"
                labelWidth="max-content"
                rows={retention.map((r, i) => ({
                  key: r.key, label: r.label, fraction: fractions[i], tone: 'light',
                  valueText: `${count(r.count)} · ${formatMetaPct(r.pct)}`,
                }))}
              />
            </>
          ) : null}
          <MetaStatRows
            testId="meta-interactions-rates"
            rows={[
              stat('hook', <HelpLabel text="Tasa de reproducciones de 3 segundos sobre impresiones" help="Reproducciones de 3 segundos ÷ impresiones." />, formatMetaPct(video.hookRatePct)),
              stat('thruplay', <HelpLabel text="Tasa de ThruPlay sobre impresiones" help="Reproducciones ThruPlay ÷ impresiones." />, formatMetaPct(video.thruplayRatePct)),
            ]}
          />
        </>
      )}
    </MetaCard>
  );
}

function InteractionsBody({ read }) {
  const { currency } = read;
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.25 }} data-testid="meta-interactions-reach">
        {read.reach != null ? <MetaStatTile value={count(read.reach)} label="Alcance (personas distintas)" testId="meta-interactions-reach-tile" /> : null}
        <MetaStatTile value={count(read.impressions)} label="Impresiones" testId="meta-interactions-impressions-tile" />
      </Box>
      <MetaCard title="Interacciones" testId="meta-interactions-card">
        <Box sx={{ display: 'flex', gap: 1.25 }}>
          <MetaStatTile value={count(read.totalInteractions)} label="Interacciones con la publicación" testId="meta-interactions-total" />
          {read.costPerEngagement != null && currency ? (
            <MetaStatTile value={metaCostText(read.costPerEngagement, currency)} label="Costo por interacción" testId="meta-interactions-cost" />
          ) : null}
        </Box>
        <MetaGroupLabel>Reacciones y comentarios</MetaGroupLabel>
        <MetaStatRows
          testId="meta-interactions-reactions"
          rows={[
            stat('reactions', 'Reacciones', count(read.reactions)),
            stat('comments', 'Comentarios', count(read.comments)),
            stat('shares', 'Veces compartida', count(read.shares)),
            stat('saves', 'Veces guardada', count(read.saves)),
            stat('pageLikes', 'Me gusta de la página', count(read.pageLikes)),
          ]}
        />
        <MetaGroupLabel>Clics y visitas</MetaGroupLabel>
        <MetaStatRows
          testId="meta-interactions-clicks"
          rows={[
            stat('linkClicks', 'Clics en el enlace', count(read.linkClicks)),
            stat('clicks', 'Clics (todos)', count(read.clicks)),
            stat('outbound', 'Clics salientes', count(read.outboundClicks)),
            stat('lpv', 'Visitas a la página de destino', count(read.landingPageViews)),
          ]}
        />
      </MetaCard>
      <VideoCard read={read} />
    </>
  );
}

/**
 * Análisis · Interacciones. De una campaña: `getcampaignmetrics?campaign_id=` (`getmetaresults` NO
 * acepta `campaign_id`); sin campaña: `getcampaignmetrics` de todo el espacio (misma forma).
 * Lista de números, sin gráficos apilados: «Interacciones con la publicación» no es la suma de
 * reacciones, comentarios, etc.
 */
export default function MetaInteractionsView({ campaigns, scopeCampaignId, period, ownerUserId = null }) {
  const campaignId = validMetaScope(campaigns, scopeCampaignId);
  const state = useMetaRead(
    (signal) => getCampaignMetrics({ period, campaignId: campaignId || undefined, ownerUserId, signal }),
    { deps: [period?.since, period?.until, campaignId, ownerUserId] },
  );
  const read = useMemo(() => readMetaInteractions(state.res), [state.res]);
  if (state.status === 'unavailable' || (state.status === 'ready' && !read.available)) {
    return (
      <PanelEmptyState
        icon={ThumbUpOffAltIcon}
        title="Todavía no hay datos de interacciones"
        hint="Cuando haya datos para el período elegido, los vas a ver acá."
        sx={{ minHeight: 240 }}
        testId="meta-analysis-interactions-empty"
      />
    );
  }
  return (
    <MetaBlockState status={state.status} error={state.error} onRetry={state.reload} testId="meta-interactions-state" skeleton={<MetaInteractionsSkeleton />}>
      <InteractionsBody read={read} />
    </MetaBlockState>
  );
}
